import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { View, StyleSheet, Platform, TextInput, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useChatStore, useDeckStore, useToastStore, useModalStore } from '@/store';
import { chatsApi } from '@/lib/api';
import socketManager from '@/lib/socket';
import { hasAvailableCredits } from '@/constants/utils/trial';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import ChatView from '@/components/Chat/ChatView';
import DeckColumnHeader from './DeckColumnHeader';
import DeckChatPicker from './DeckChatPicker';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const FOCUS_BLUE = '#3b82f6';
const COMPOSE_FOCUS_DELAY_MS = 100;
const SOCKET_KICK_DELAY_MS = 250;
const CHAT_DELETED_PROBE_MS = 500;

const DeckColumn = React.memo(function DeckColumn({
  column,
  deckSlug,
  columnIndex,
  totalColumns,
  isFocused = false,
  onDragStart,
  setRef,
  isBeingDragged = false,
  isAnimatingIn = false,
  isRemoving = false,
}) {
  const { colors } = useTheme();
  const { conversationId, width } = column;
  const columnId = column.id;
  // Selecting `state.actions` directly is safe: zustand stores keep the
  // same reference across renders, so we don't churn subscriptions.
  const deckActions = useDeckStore(state => state.actions);
  const { removeColumn, setColumnConversation, setFocusedColumnId } = deckActions;

  const handleContainerRef = useCallback((node) => {
    setRef?.(columnId, node);
  }, [setRef, columnId]);

  const handleDragStart = useCallback((e) => {
    onDragStart?.(columnId, e);
  }, [onDragStart, columnId]);

  const chat = useChatStore(state =>
    conversationId ? state.chats.find(c => c.id === conversationId) : null
  );
  const addChats = useChatStore(state => state.addChats);
  const createConversation = useChatStore(state => state.createConversation);
  const userId = useAuthStore(state => state.user?.id);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable ?? 0);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatDeleted, setChatDeleted] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const sendPendingRef = useRef(false);
  const composeInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setChatDeleted(false);
    setIsLoading(false);
    if (!conversationId) return;

    const fetchChat = async () => {
      try {
        const freshChats = useChatStore.getState().chats;
        const existing = freshChats.find(c => c.id === conversationId);
        if (existing && existing.messages?.length > 0) {
          setIsLoading(false);
          setChatDeleted(false);
          return;
        }

        setIsLoading(true);
        setError(null);
        setChatDeleted(false);

        const data = await chatsApi.getConversationById(conversationId);
        if (cancelled) return;

        if (!data) {
          throw new Error('Chat not found');
        }

        const freshAfterFetch = useChatStore.getState().chats;
        const addedWhileFetching = freshAfterFetch.find(c => c.id === conversationId);
        if (addedWhileFetching && addedWhileFetching.messages?.length > 0) {
          setIsLoading(false);
          return;
        }

        addChats([data]);
      } catch (err) {
        if (cancelled) return;
        console.error('[DeckColumn] Error fetching chat:', err);
        if (err.status === 404) {
          setChatDeleted(true);
        } else {
          setError(err.message || 'Failed to load chat');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchChat();
    return () => { cancelled = true; };
  }, [conversationId, addChats]);

  useEffect(() => {
    if (conversationId && !chat && !isLoading && !error) {
      const timer = setTimeout(() => {
        const freshChat = useChatStore.getState().chats.find(c => c.id === conversationId);
        if (!freshChat) {
          setChatDeleted(true);
        }
      }, CHAT_DELETED_PROBE_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [conversationId, chat, isLoading, error]);

  const handleRemove = useCallback(() => {
    removeColumn(columnId, deckSlug);
  }, [columnId, deckSlug, removeColumn]);

  const handleSelectChat = useCallback((chatId) => {
    setColumnConversation(columnId, chatId, deckSlug);
    setChatDeleted(false);
    setError(null);
  }, [columnId, deckSlug, setColumnConversation]);

  const handleChangeChat = useCallback(() => {
    setColumnConversation(columnId, null, deckSlug);
    setChatDeleted(false);
    setError(null);
    setIsComposing(false);
    setComposeText('');
  }, [columnId, deckSlug, setColumnConversation]);

  // Focus belongs to the mounted input. A created conversation still
  // needs its socket kick after the user switches decks.
  const focusTimerRef = useRef(null);
  useEffect(() => () => {
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
  }, []);

  const handleNewConversation = useCallback(() => {
    setIsComposing(true);
    setComposeText('');
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    focusTimerRef.current = setTimeout(() => {
      composeInputRef.current?.focus();
    }, COMPOSE_FOCUS_DELAY_MS);
  }, []);

  const handleComposeSend = useCallback(async () => {
    const text = composeText.trim();
    if (!text || sendPendingRef.current) return;
    sendPendingRef.current = true;
    setIsSending(true);
    try {
      if (!(await socketManager.requireConnection())) return;

      if (!isPlus && !isGhost && !hasAvailableCredits(totalAvailableCredits)) {
        openModal('OutOfCreditsModal', {
          title: "Out of credits",
          subtitle: `You've used all your free credits. Upgrade to Plus for free default model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`,
        });
        return;
      }

      const formattedMessage = {
        text,
        modelIds: [],
        agentIds: [],
        actions: [],
        fileIds: [],
        parameters: {},
      };

      const conversation = await createConversation({
        userId,
        formattedMessage,
      });

      if (!conversation?.id) {
        throw new Error('Failed to create conversation');
      }

      setTimeout(() => {
        socketManager.emitKickConversation(userId, conversation.id);
      }, SOCKET_KICK_DELAY_MS);

      setColumnConversation(columnId, conversation.id, deckSlug);
      setIsComposing(false);
      setComposeText('');
    } catch (err) {
      console.error('[DeckColumn] Error creating conversation:', err);
      addToast(err.message || 'Failed to create conversation', 'error');
    } finally {
      sendPendingRef.current = false;
      setIsSending(false);
    }
  }, [composeText, userId, createConversation, columnId, deckSlug, setColumnConversation, addToast, isPlus, isGhost, totalAvailableCredits, openModal]);

  const handleComposeCancel = useCallback(() => {
    setIsComposing(false);
    setComposeText('');
  }, []);

  const handleComposeKeyPress = useCallback((e) => {
    if (e.nativeEvent?.key === 'Enter' && !e.nativeEvent?.shiftKey) {
      e.preventDefault();
      handleComposeSend();
    }
  }, [handleComposeSend]);

  const handleFocus = useCallback(() => {
    setFocusedColumnId(columnId);
  }, [columnId, setFocusedColumnId]);

  const containerStyle = useMemo(() => [
    styles.container,
    {
      width: isRemoving ? 0 : width,
      borderRightColor: colors.border_color_secondary,
      opacity: isAnimatingIn || isRemoving ? 0 : isBeingDragged ? 0.5 : 1,
      overflow: isRemoving ? 'hidden' : undefined,
    }
  ], [width, colors.border_color_secondary, isBeingDragged, isAnimatingIn, isRemoving]);

  if (!conversationId || chatDeleted) {
    return (
      <View ref={handleContainerRef} style={containerStyle} onPointerDown={handleFocus}>
        <DeckColumnHeader
          chat={null}
          columnId={columnId}
          columnIndex={columnIndex}
          totalColumns={totalColumns}
          onRemove={handleRemove}
          onChangeChat={isComposing ? handleComposeCancel : handleChangeChat}
          isFocused={isFocused}
          onDragStart={handleDragStart}
        />
        {isComposing ? (
          <View style={[composeStyles.container, { backgroundColor: colors.solid_color_primary }]}>
            <View style={composeStyles.body}>
              <Icon name="message" size={28} color={colors.text_color_tertiary} />
              <Text size="s" color="secondary" style={composeStyles.label}>
                Type your first message to start a new conversation
              </Text>
            </View>
            <View style={[composeStyles.inputRow, { borderTopColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
              <TextInput
                ref={composeInputRef}
                value={composeText}
                onChangeText={setComposeText}
                placeholder="Type a message..."
                placeholderTextColor={colors.text_color_tertiary}
                style={[composeStyles.input, { color: colors.text_color_primary }]}
                multiline
                onKeyPress={handleComposeKeyPress}
                editable={!isSending}
              />
              <Pressable
                onPress={handleComposeSend}
                disabled={!composeText.trim() || isSending}
                accessibilityRole="button"
                accessibilityLabel="Send message"
                style={({ hovered }) => [
                  composeStyles.sendButton,
                  {
                    backgroundColor: composeText.trim() && !isSending ? FOCUS_BLUE : colors.solid_color_hover,
                    opacity: hovered && composeText.trim() ? 0.85 : 1,
                  },
                ]}
              >
                <Icon name="arrow-up" size={14} color={composeText.trim() && !isSending ? '#fff' : colors.text_color_tertiary} />
              </Pressable>
            </View>
          </View>
        ) : (
          <DeckChatPicker
            onSelectChat={handleSelectChat}
            onNewConversation={handleNewConversation}
          />
        )}
      </View>
    );
  }

  const showLoading = !chat && !error;

  return (
    <View ref={handleContainerRef} style={containerStyle} onPointerDown={handleFocus}>
      <DeckColumnHeader
        chat={chat}
        columnId={columnId}
        columnIndex={columnIndex}
        totalColumns={totalColumns}
        onRemove={handleRemove}
        onChangeChat={handleChangeChat}
        isFocused={isFocused}
        onDragStart={handleDragStart}
      />
      <ChatView
        conversationId={conversationId}
        chat={chat}
        isLoading={showLoading || isLoading}
        error={error}
        isCompact={true}
        onFocus={handleFocus}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    height: '100%',
    borderRightWidth: 1,
    flexShrink: 0,
    flexDirection: 'column',
    ...(Platform.OS === 'web' && {
      display: 'flex',
      transition: 'opacity 0.2s ease, width 0.2s ease',
    }),
  },
});

const composeStyles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'space-between',
  },
  body: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    gap: 12,
  },
  label: {
    textAlign: 'center',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    padding: 12,
    borderTopWidth: 1,
  },
  input: {
    flex: 1,
    fontSize: 14,
    maxHeight: 120,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
  },
  sendButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
});

export default DeckColumn;
